import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import apiClient from '../utils/api';

const RegisterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const RegisterCard = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 480px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    border-radius: 20px;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: #000000;
  margin-bottom: 2rem;
  font-size: 2rem;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.75rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  text-align: left;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const RadioRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const SuccessMessage = styled.div`
  color: #0f5132;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #d1e7dd;
  border: 1px solid #badbcc;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const Notice = styled.div<{ variant?: 'info' | 'warning' }>`
  background: ${props => props.variant === 'warning' ? '#ffe5e5' : '#e7f5ff'};
  border: 1px solid ${props => props.variant === 'warning' ? '#ff6b6b' : '#339af0'};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const NoticeTitle = styled.div<{ variant?: 'info' | 'warning' }>`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.variant === 'warning' ? '#c92a2a' : '#1971c2'};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const NoticeText = styled.div<{ variant?: 'info' | 'warning' }>`
  font-size: 0.875rem;
  color: ${props => props.variant === 'warning' ? '#c92a2a' : '#1971c2'};
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ConsentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConsentLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.75rem;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ConsentBox = styled.div`
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  max-height: 120px;
  overflow-y: auto;
  background: #f8f9fa;
  margin-bottom: 0.75rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #e9ecef;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #6c757d;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    max-height: 100px;
  }
`;

const ConsentText = styled.div`
  font-size: 0.8125rem;
  color: #495057;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.9375rem;
  color: #343a40;
  cursor: pointer;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2.5rem 2rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
`;

const ModalIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ModalMessage = styled.div`
  font-size: 1rem;
  color: #495057;
  line-height: 1.6;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const ModalHighlight = styled.div`
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;
  text-align: left;
`;

const ModalHighlightText = styled.div`
  font-size: 0.875rem;
  color: #856404;
  line-height: 1.6;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 1rem;
  text-decoration: underline;
  
  &:hover {
    color: #000000;
  }
`;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    birthYear: '',
    address: '',
    gender: '',
    electionStatus: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [filmingAgreed, setFilmingAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    const limited = numbers.slice(0, 11);
    
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 7) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    // 전화번호 자동 포맷팅
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    // 생년월일: 숫자만 입력, 6자리 제한
    if (field === 'birthYear') {
      const numbers = value.replace(/[^\d]/g, '');
      formattedValue = numbers.slice(0, 6);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 유효성 검사
    if (!formData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('전화번호를 입력해주세요.');
      return;
    }
    
    if (!formData.birthYear.trim()) {
      setError('생년월일을 입력해주세요.');
      return;
    }
    
    if (formData.birthYear.length !== 6) {
      setError('생년월일은 6자리로 입력해주세요. (예: 231105)');
      return;
    }
    
    if (!formData.address.trim()) {
      setError('주소를 입력해주세요.');
      return;
    }
    
    if (!formData.gender) {
      setError('성별을 선택해주세요.');
      return;
    }
    
    if (!formData.electionStatus) {
      setError('선출 여부를 선택해주세요.');
      return;
    }
    
    if (!filmingAgreed) {
      setError('촬영 및 SNS 게시 동의를 확인해주세요.');
      return;
    }
    
    if (!privacyAgreed) {
      setError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.post('/members/apply', {
        name: formData.name,
        phone: formData.phone,
        birth_year: formData.birthYear,
        address: formData.address,
        gender: formData.gender,
        election_status: formData.electionStatus
      });

      if (response.data.success) {
        setShowSuccessModal(true);
      }
    } catch (err: any) {
      console.error('가입신청 오류:', err);
      const errorMessage = err.response?.data?.detail || '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Title>H_B 가입신청</Title>
        
        <Notice variant="info">
          <NoticeTitle variant="info">
            ℹ️ 가입 절차 안내
          </NoticeTitle>
          <NoticeText variant="info">
            - 가입신청 후 관리자 승인이 완료되면 회원 명부에 등록됩니다.
            <br />
            - 승인된 회원에 한하여 로그인 계정을 만들 수 있습니다.
          </NoticeText>
        </Notice>
        
        <Form onSubmit={handleSubmit}>
          <Input
            label="이름"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="이름을 입력하세요"
            required
          />
          
          <Input
            label="전화번호"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="010-0000-0000"
            required
          />
          
          <Input
            label="생년월일"
            type="text"
            value={formData.birthYear}
            onChange={(e) => handleInputChange('birthYear', e.target.value)}
            placeholder="6자리 (예: 231105)"
            required
          />
          
          <Input
            label="주소"
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="예: 경기도 수원시 (상세주소X)"
            required
          />
          
          <RadioRow>
            <RadioGroup>
              <RadioLabel>성별 <span style={{ color: '#dc3545' }}>*</span></RadioLabel>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value="남자"
                    checked={formData.gender === '남자'}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  />
                  남자
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value="여자"
                    checked={formData.gender === '여자'}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  />
                  여자
                </RadioOption>
              </RadioOptions>
            </RadioGroup>
            
            <RadioGroup>
              <RadioLabel>선출 여부 <span style={{ color: '#dc3545' }}>*</span></RadioLabel>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="electionStatus"
                    value="선출"
                    checked={formData.electionStatus === '선출'}
                    onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                  />
                  선출
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="electionStatus"
                    value="비선출"
                    checked={formData.electionStatus === '비선출'}
                    onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                  />
                  비선출
                </RadioOption>
              </RadioOptions>
            </RadioGroup>
          </RadioRow>
          
          <ConsentSection>
            <ConsentLabel>촬영 및 SNS 게시 동의</ConsentLabel>
            <ConsentBox>
              <ConsentText>
                H_B는 매주 훈련 및 경기 영상을 촬영하고 있습니다.
                <br /><br />
                촬영된 사진과 영상은 <a 
                  href="https://www.instagram.com/hb_volleyball_club?igsh=Ymh2bGRhZHA1Zm0x&utm_source=qr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#000000', fontWeight: '600', textDecoration: 'underline' }}
                >
                  H_B 인스타그램
                </a>에 게시될 수 있으며,
                <br />
                홍보 및 활동 기록 목적으로만 사용됩니다.
                <br />
                촬영에 동의하지 않을 경우, 게스트 참여가 제한될 수 있으니
                사전에 양해 부탁드립니다.
              </ConsentText>
            </ConsentBox>
            
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="filming"
                checked={filmingAgreed}
                onChange={(e) => setFilmingAgreed(e.target.checked)}
              />
              <CheckboxLabel htmlFor="filming">
                촬영 및 SNS 게시에 동의합니다 (필수)
              </CheckboxLabel>
            </CheckboxWrapper>
          </ConsentSection>
          
          <ConsentSection>
            <ConsentLabel>개인정보 수집 및 이용 동의</ConsentLabel>
            <ConsentBox>
              <ConsentText>
                <strong>수집 목적:</strong> H_B 배구동호회의 회원 관리 및 운영
                <br /><br />
                <strong>수집 항목:</strong> 이름, 전화번호, 생년월일, 주소, 성별, 선출 여부
                <br /><br />
                <strong>보유 기간:</strong> 영구 보관
                <br /><br />
                귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있으며, 
                동의하지 않을 경우 가입신청이 제한될 수 있습니다.
                <br /><br />
                수집된 개인정보는 동호회 회원 관리 및 연락 목적으로만 사용되며, 
                제3자에게 제공되지 않습니다.
              </ConsentText>
            </ConsentBox>
            
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="privacy"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
              />
              <CheckboxLabel htmlFor="privacy">
                개인정보 수집 및 이용에 동의합니다 (필수)
              </CheckboxLabel>
            </CheckboxWrapper>
          </ConsentSection>
          
          <Notice variant="warning">
            <NoticeTitle variant="warning">
              💰 회비 안내
            </NoticeTitle>
            <NoticeText variant="warning">
              💳 월 회비 : 2만원
              <br />
              💳 가입비 : 8만원 (회비 + 유니폼비 포함)
              <br />
              ✔️ 매월 10일까지 납부
              <br />
              ✔️ 1년 또는 분기 일괄납부 가능
              <br />
              ✔️ 납부 시 입금명 : '이름' 
            </NoticeText>
          </Notice>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={loading}
          >
            {loading ? '신청 중...' : '가입신청하기'}
          </Button>
        </Form>
        
        <BackButton onClick={() => navigate('/')}>
          돌아가기
        </BackButton>
      </RegisterCard>
      
      {/* 가입신청 완료 모달 */}
      <ModalOverlay isOpen={showSuccessModal}>
        <ModalContent>
          <ModalIcon>✅</ModalIcon>
          <ModalTitle>가입신청이 완료되었습니다!</ModalTitle>
          <ModalMessage>
            신청해주셔서 감사합니다.
          </ModalMessage>
          
          <ModalHighlight>
            <ModalHighlightText>
              💳 회비 입금 확인 후 연락드리겠습니다.
              <br /><br />
              📞 문의사항이 있으시면 매니저에게 연락 부탁드립니다.
            </ModalHighlightText>
          </ModalHighlight>
          
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/');
            }}
          >
            확인
          </Button>
        </ModalContent>
      </ModalOverlay>
    </RegisterContainer>
  );
};

export default Register;

